import React, { useState } from "react";
import { Typography, message } from "antd";
import UserAvatar from "../assests/user_avatar.png";

const { Title, Paragraph } = Typography;

// Colorful Paper Confetti Particles Configuration
const CONFETTI_PIECES = [
  { color: "#ffd700", shape: "rect", left: "8%", delay: "0.1s", duration: "3.2s", size: "10px" },
  { color: "#ff2a5f", shape: "circle", left: "18%", delay: "0.6s", duration: "4.0s", size: "12px" },
  { color: "#00f2fe", shape: "ribbon", left: "28%", delay: "0.3s", duration: "3.5s", size: "14px" },
  { color: "#39ff14", shape: "rect", left: "38%", delay: "1.0s", duration: "3.8s", size: "9px" },
  { color: "#ff9100", shape: "circle", left: "48%", delay: "0.4s", duration: "3.4s", size: "11px" },
  { color: "#b5179e", shape: "ribbon", left: "58%", delay: "0.8s", duration: "4.2s", size: "13px" },
  { color: "#00e5ff", shape: "rect", left: "68%", delay: "0.2s", duration: "3.6s", size: "10px" },
  { color: "#ff007f", shape: "circle", left: "78%", delay: "1.2s", duration: "3.9s", size: "12px" },
  { color: "#ffea00", shape: "ribbon", left: "88%", delay: "0.5s", duration: "3.3s", size: "14px" },
  { color: "#76ff03", shape: "rect", left: "94%", delay: "0.9s", duration: "4.1s", size: "8px" },
];

export default function BirthDayTemplate({ employee }) {
  const [imgErr, setImgErr] = useState(false);

  // Full Name Resolution: Ensures complete first + last name is shown
  const employeeName = (() => {
    const fn = employee?.user?.first_name || employee?.first_name || "";
    const ln = employee?.user?.last_name || employee?.last_name || "";
    const combined = `${fn} ${ln}`.trim();
    if (combined) return combined;
    return employee?.full_name || employee?.employee_name || employee?.name || "Valued Teammate";
  })();

  const organizationName =
    employee?.organisation_name ||
    employee?.organisation?.name ||
    "Baashyaam Group Family";

  const departmentName =
    employee?.department_name ||
    employee?.department?.name ||
    employee?.department ||
    "Department";

  const designation =
    employee?.designation_name ||
    employee?.designation?.name ||
    employee?.designation ||
    "Team Member";

  const rawPhoto = employee?.photo || employee?.profile_photo || employee?.user?.photo;
  const photoSrc = !imgErr && rawPhoto ? rawPhoto : UserAvatar;

  const copyWishText = () => {
    const wish = `🎉 Happy Birthday to ${employeeName}! 🎂\nWishing you a magnificent year filled with success, health, and happiness!\n- Warm wishes from ${organizationName}`;
    navigator.clipboard.writeText(wish);
    message.success("Birthday wish copied to clipboard!");
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @keyframes paperPopBounce {
          0% { opacity: 0; transform: scale(0.6) translateY(40px) rotate(-5deg); }
          60% { opacity: 1; transform: scale(1.04) translateY(-6px) rotate(2deg); }
          100% { opacity: 1; transform: scale(1) translateY(0) rotate(0deg); }
        }
        @keyframes paperConfettiFlutter {
          0% {
            transform: translateY(-20px) rotate(0deg) rotateX(0deg);
            opacity: 0;
          }
          15% { opacity: 1; }
          85% { opacity: 1; }
          100% {
            transform: translateY(580px) rotate(720deg) rotateX(360deg);
            opacity: 0;
          }
        }
        @keyframes crownFloatPhysics {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-8px) rotate(3deg); }
        }
        @keyframes photoGoldGlow {
          0%, 100% { box-shadow: 0 10px 25px rgba(0,0,0,0.5), 0 0 20px rgba(212, 160, 55, 0.4); }
          50% { box-shadow: 0 14px 35px rgba(0,0,0,0.65), 0 0 35px rgba(212, 160, 55, 0.75); }
        }
        .paper-confetti-item {
          position: absolute;
          top: -15px;
          pointer-events: none;
          z-index: 5;
          animation: paperConfettiFlutter linear infinite;
        }
        .bday-card-pop-wrapper {
          animation: paperPopBounce 0.8s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
        .bday-action-btn-v3 {
          padding: 8px 20px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          display: inline-flex;
          align-items: center;
          gap: 6px;
        }
        .bday-action-btn-v3:hover {
          transform: translateY(-2px) scale(1.03);
        }
      `}</style>

      <div
        className="bday-card-pop-wrapper"
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 16,
          padding: "10px 0",
          fontFamily: "'Poppins', Georgia, serif",
        }}
      >
        {/* Main Birthday Card Shell */}
        <div
          style={{
            position: "relative",
            width: "400px",
            height: "600px",
            borderRadius: "20px",
            background: "linear-gradient(145deg, #07162c 0%, #0d2847 45%, #183e6b 100%)",
            border: "2.5px solid #d4a037",
            boxShadow: "0 20px 50px rgba(0,0,0,0.45), 0 0 30px rgba(212, 160, 55, 0.25)",
            overflow: "hidden",
            padding: "24px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "space-between",
            boxSizing: "border-box",
            color: "#ffffff",
          }}
        >
          {/* Animated Colorful Paper Confetti Pieces */}
          {CONFETTI_PIECES.map((item, idx) => (
            <div
              key={idx}
              className="paper-confetti-item"
              style={{
                left: item.left,
                width: item.size,
                height: item.shape === "ribbon" ? "18px" : item.size,
                background: item.color,
                borderRadius: item.shape === "circle" ? "50%" : item.shape === "ribbon" ? "3px" : "2px",
                animationDuration: item.duration,
                animationDelay: item.delay,
              }}
            />
          ))}

          {/* Inner Frame Lines */}
          <div
            style={{
              position: "absolute",
              inset: 10,
              border: "1px solid rgba(212, 160, 55, 0.4)",
              borderRadius: 14,
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 14,
              border: "1px dashed rgba(212, 160, 55, 0.25)",
              borderRadius: 10,
              pointerEvents: "none",
            }}
          />

          {/* Background Radial Glow */}
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundImage: `radial-gradient(circle at 50% 22%, rgba(212, 160, 55, 0.22) 0%, transparent 60%),
                                radial-gradient(circle at 20% 80%, rgba(255, 255, 255, 0.08) 0%, transparent 40%)`,
              pointerEvents: "none",
            }}
          />

          {/* Top Crown & Header */}
          <div style={{ textAlign: "center", zIndex: 3, marginTop: 4 }}>
            <div style={{ fontSize: 30, animation: "crownFloatPhysics 3.2s infinite ease-in-out" }}>👑</div>
            <div
              style={{
                fontSize: 10,
                fontWeight: 900,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: "#f5d77f",
                marginBottom: 2,
              }}
            >
              HAPPY BIRTHDAY
            </div>
            <div
              style={{
                fontSize: 21,
                fontWeight: 900,
                fontFamily: "Georgia, serif",
                background: "linear-gradient(135deg, #ffffff 0%, #f5d77f 50%, #d4a037 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "0.02em",
              }}
            >
              CELEBRATING YOU
            </div>
          </div>

          {/* Photo Frame Container */}
          <div style={{ position: "relative", zIndex: 3, margin: "4px 0" }}>
            <div
              style={{
                width: 175,
                height: 175,
                borderRadius: "50%",
                padding: 6,
                background: "linear-gradient(135deg, #ffe89e 0%, #d4a037 50%, #8a6012 100%)",
                animation: "photoGoldGlow 3.5s ease-in-out infinite",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src={photoSrc}
                onError={() => setImgErr(true)}
                alt={employeeName}
                style={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "3px solid #0d2847",
                }}
              />
            </div>
            {/* Special Day Ribbon */}
            <div
              style={{
                position: "absolute",
                bottom: -10,
                left: "50%",
                transform: "translateX(-50%)",
                background: "linear-gradient(135deg, #d4a037 0%, #b8821f 100%)",
                color: "#07162c",
                fontWeight: 900,
                fontSize: 10,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                padding: "4px 16px",
                borderRadius: 14,
                boxShadow: "0 4px 14px rgba(0,0,0,0.35)",
                whiteSpace: "nowrap",
                border: "1px solid #ffe89e",
              }}
            >
              ✨ SPECIAL DAY ✨
            </div>
          </div>

          {/* Employee FULL NAME & Details Block (Without truncation) */}
          <div style={{ textAlign: "center", zIndex: 3, padding: "0 10px", width: "100%", boxSizing: "border-box" }}>
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: 22,
                fontWeight: 900,
                fontFamily: "Georgia, serif",
                color: "#ffffff",
                textShadow: "0 2px 10px rgba(0,0,0,0.5)",
                lineHeight: 1.25,
                wordBreak: "break-word",
              }}
            >
              {employeeName}
            </Title>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#f5d77f",
                marginTop: 4,
                wordBreak: "break-word",
              }}
            >
              {designation}
            </div>
            <div style={{ fontSize: 11, color: "#cbd5e1", marginTop: 2, fontWeight: 500 }}>
              {departmentName}
            </div>
          </div>

          {/* Birthday Wish & Org Branding */}
          <div style={{ textAlign: "center", zIndex: 3, paddingBottom: 4 }}>
            <Paragraph
              style={{
                margin: "0 0 10px 0",
                fontSize: 12,
                color: "#e2e8f0",
                fontStyle: "italic",
                fontFamily: "Georgia, serif",
                lineHeight: 1.4,
                padding: "0 10px",
              }}
            >
              "Wishing you a day filled with happiness, triumph, and endless joy!"
            </Paragraph>

            <div
              style={{
                background: "rgba(212, 160, 55, 0.16)",
                border: "1px solid rgba(212, 160, 55, 0.4)",
                borderRadius: 20,
                padding: "5px 18px",
                display: "inline-block",
              }}
            >
              <span style={{ fontSize: 11, fontWeight: 800, color: "#f5d77f" }}>
                {organizationName}
              </span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
